import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Radio,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { getPublicRoster, createInvitation, getUserOpportunities } from '../api/api';

const PublicRoster = ({ token, domain, onNavigateToOpportunities, canInvite = true, currentUserId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    day: '',
    timeSlot: '',
    activity: ''
  });
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [expandedAbout, setExpandedAbout] = useState({});
  const [revealedContact, setRevealedContact] = useState({});
  
  // Invitation dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [loadingOpportunities, setLoadingOpportunities] = useState(false);
  const [inviting, setInviting] = useState(false);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const timeSlots = ['morning', 'day', 'evening'];
  const activities = ['cruising', 'racing'];

  const loadRoster = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPublicRoster(token, domain, filters);
      setRoster(data);
    } catch (error) {
      console.error('Error loading roster:', error);
    } finally {
      setLoading(false);
    }
  }, [token, domain, filters]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      day: '',
      timeSlot: '',
      activity: ''
    });
  };

  const handleInviteClick = async (crew) => {
    setSelectedCrew(crew);
    setSelectedOpportunity(null);
    setInviteDialogOpen(true);
    
    // Load user's active opportunities
    setLoadingOpportunities(true);
    try {
      const opps = await getUserOpportunities(token, true);
      // Filter to only active, non-expired opportunities
      const activeOpps = opps.filter(opp => 
        opp.isActive && new Date(opp.dateTime) > new Date()
      );
      setOpportunities(activeOpps);
    } catch (error) {
      console.error('Error loading opportunities:', error);
    } finally {
      setLoadingOpportunities(false);
    }
  };

  const handleInviteConfirm = async () => {
    if (!selectedOpportunity || !selectedCrew) return;
    
    setInviting(true);
    try {
      await createInvitation(token, selectedOpportunity._id, selectedCrew.userId);
      setInviteDialogOpen(false);
      // Optionally show success message
    } catch (error) {
      console.error('Error creating invitation:', error);
    } finally {
      setInviting(false);
    }
  };

  const handleCreateOpportunity = () => {
    setInviteDialogOpen(false);
    onNavigateToOpportunities();
  };

  const getDisplayName = (crew) => {
    const firstName = crew.firstName || '';
    const lastInitial = crew.lastName ? crew.lastName.charAt(0) + '.' : '';
    return `${firstName} ${lastInitial}`.trim();
  };

  // Returns a human-friendly "last seen" string, or null when there's no
  // usable info (missing or older than 30 days -> shown as "long time ago").
  const getLastSeenText = (lastSeen) => {
    if (!lastSeen) return t('roster.lastSeen.longAgo');

    const seen = new Date(lastSeen);
    if (isNaN(seen.getTime())) return t('roster.lastSeen.longAgo');

    // Compare at calendar-day granularity in local time.
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfSeenDay = new Date(seen);
    startOfSeenDay.setHours(0, 0, 0, 0);

    const dayDiff = Math.round((startOfToday - startOfSeenDay) / (1000 * 60 * 60 * 24));

    if (dayDiff <= 0) return t('roster.lastSeen.today');
    if (dayDiff === 1) return t('roster.lastSeen.dayAgo');
    if (dayDiff <= 30) return t('roster.lastSeen.daysAgo', { count: dayDiff });
    return t('roster.lastSeen.longAgo');
  };

  const hasActiveFilters = filters.day || filters.timeSlot || filters.activity;

  return (
    <Box sx={{ padding: isMobile ? '10px' : '20px' }}>
      {/* Header with filters toggle for mobile */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" color="text.secondary">
          {t('roster.title')} ({roster.length})
        </Typography>
        {isMobile && (
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterListIcon color={hasActiveFilters ? 'primary' : 'inherit'} />
          </IconButton>
        )}
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('roster.filters.day')}</InputLabel>
                <Select
                  value={filters.day}
                  label={t('roster.filters.day')}
                  onChange={(e) => handleFilterChange('day', e.target.value)}
                >
                  <MenuItem value="">{t('roster.filters.allDays')}</MenuItem>
                  {days.map(day => (
                    <MenuItem key={day} value={day}>
                      {t(`profile.availability.days.${day}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('roster.filters.timeSlot')}</InputLabel>
                <Select
                  value={filters.timeSlot}
                  label={t('roster.filters.timeSlot')}
                  onChange={(e) => handleFilterChange('timeSlot', e.target.value)}
                >
                  <MenuItem value="">{t('roster.filters.allTimeSlots')}</MenuItem>
                  {timeSlots.map(slot => (
                    <MenuItem key={slot} value={slot}>
                      {t(`profile.availability.timeSlots.${slot}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('roster.filters.activity')}</InputLabel>
                <Select
                  value={filters.activity}
                  label={t('roster.filters.activity')}
                  onChange={(e) => handleFilterChange('activity', e.target.value)}
                >
                  <MenuItem value="">{t('roster.filters.allActivities')}</MenuItem>
                  {activities.map(activity => (
                    <MenuItem key={activity} value={activity}>
                      {t(`profile.availability.${activity}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              {hasActiveFilters && (
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  {t('roster.filters.clear')}
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Roster List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : roster.length === 0 ? (
        <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
          {t('roster.noCrewAvailable')}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {roster.map((crew) => (
            <Grid item xs={12} sm={6} key={crew.userId}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  m: 1,
                  mb: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar
                    src={crew.profilePictureUrl}
                    alt={getDisplayName(crew)}
                    onClick={() => navigate(`/profile/${crew.userId}`)}
                    sx={{ 
                      width: 64, 
                      height: 64, 
                      mr: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      {getDisplayName(crew)}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {crew.activityPreference?.cruising && (
                        <Chip 
                          label={t('profile.availability.cruising')} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                      {crew.activityPreference?.racing && (
                        <Chip 
                          label={t('profile.availability.racing')} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {t('roster.lastSeen.label')}: {getLastSeenText(crew.lastSeen)}
                    </Typography>
                  </Box>
                </Box>
                
                {crew.aboutYou && (
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: expandedAbout[crew.userId] ? 'unset' : 3,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {crew.aboutYou}
                    </Typography>
                    {crew.aboutYou.length > 150 && (
                      <Button
                        size="small"
                        onClick={() => setExpandedAbout(prev => ({
                          ...prev,
                          [crew.userId]: !prev[crew.userId]
                        }))}
                        endIcon={expandedAbout[crew.userId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ mt: 0.5, p: 0, minWidth: 'auto' }}
                      >
                        {expandedAbout[crew.userId] ? t('match.showLess') : t('match.readMore')}
                      </Button>
                    )}
                  </Box>
                )}
                
                {/* Contact information if shared — hidden behind a Contact button */}
                {(crew.phoneNumber || crew.email) && (
                  <Box sx={{ mb: 2 }}>
                    {revealedContact[crew.userId] ? (
                      <>
                        {crew.phoneNumber && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {crew.phoneNumber}
                            </Typography>
                          </Box>
                        )}
                        {crew.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                              {crew.email}
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={crew.phoneNumber ? <PhoneIcon /> : <EmailIcon />}
                        onClick={() => setRevealedContact(prev => ({
                          ...prev,
                          [crew.userId]: true
                        }))}
                        fullWidth
                      >
                        {t('roster.contact')}
                      </Button>
                    )}
                  </Box>
                )}
                
                <Stack direction="row" spacing={1}>
                  {canInvite && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => handleInviteClick(crew)}
                      fullWidth
                    >
                      {t('roster.invite')}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ChatBubbleOutlineIcon />}
                    onClick={() => navigate(`/messages/${crew.userId}`)}
                    disabled={crew.userId === currentUserId}
                    fullWidth
                  >
                    {t('messages.message')}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Invite Dialog */}
      <Dialog 
        open={inviteDialogOpen} 
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('roster.inviteDialog.title', { name: selectedCrew ? getDisplayName(selectedCrew) : '' })}
        </DialogTitle>
        <DialogContent>
          {loadingOpportunities ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : opportunities.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {t('roster.inviteDialog.noOpportunities')}
              </Typography>
              <Button 
                variant="contained" 
                onClick={handleCreateOpportunity}
              >
                {t('roster.inviteDialog.createOpportunity')}
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('roster.inviteDialog.selectOpportunity')}
              </Typography>
              <List>
                {opportunities.map((opp) => (
                  <ListItem 
                    key={opp._id}
                    button
                    onClick={() => setSelectedOpportunity(opp)}
                    selected={selectedOpportunity?._id === opp._id}
                    sx={{ 
                      border: '1px solid',
                      borderColor: selectedOpportunity?._id === opp._id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemIcon>
                      <Radio checked={selectedOpportunity?._id === opp._id} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={opp.name || t('opportunity.form.name')}
                      secondary={new Date(opp.dateTime).toLocaleDateString()}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          {opportunities.length > 0 && (
            <Button 
              variant="contained" 
              onClick={handleInviteConfirm}
              disabled={!selectedOpportunity || inviting}
            >
              {inviting ? <CircularProgress size={24} /> : t('roster.inviteDialog.send')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PublicRoster;
